import {
  CheckCircleOutline as CheckIcon,
  AccessTime as ClockIcon,
  Gavel as GavelIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { apiService } from '../../services/api';

export const LATEST_SAAS_AGREEMENT_VERSION = 'v2.0 (2026-10-22)';
export const DEFAULT_V2_RELEASE_DATE = '2026-10-22T00:00:00.000Z';
export const NOTICE_PERIOD_DAYS = 30;

interface TermChangeItem {
  clause: string;
  title: string;
  tag: string;
  summary: string;
}

export const V2_TERM_CHANGES: TermChangeItem[] = [
  {
    clause: 'SaaS Agreement Clause 8.1 & 8.2 / Terms Clause 22',
    title: '100% Customer Ownership of Data & Generated Bid Outputs',
    tag: 'Customer IP Protection',
    summary:
      'Explicitly confirms that your organization retains 100% ownership of all Intellectual Property Rights in your uploaded Customer Data and in the specific bid documents, capability reports, compliance matrices, and draft responses generated for you from your Customer Data. BIDLOGIQ retains ownership strictly over the Platform, software, algorithms, and generic system templates.',
  },
  {
    clause: 'SaaS Agreement Clause 9.4',
    title: 'Google Cloud KMS Envelope Encryption & Zero-Training Warranty',
    tag: 'Data Security & AI Privacy',
    summary:
      'Codifies our cryptographic security architecture (TLS 1.3 in transit, Firestore Transparent Data Encryption, and per-file AES-256 Application-Level Envelope Encryption via Google Cloud KMS at rest) and warrants that your Customer Data, prompts, and generated outputs are processed exclusively via paid enterprise AI endpoints and are never used to train public or foundational AI models.',
  },
  {
    clause: 'SaaS Agreement Clause 12.1(k) / Terms Clause 12',
    title: 'Human-in-the-Loop (HITL) AI Supervision',
    tag: 'Agentic AI Governance',
    summary:
      'Clarifies that AI-generated capability reports, compliance matrices, and draft responses are assistive drafts produced using probabilistic machine learning technology. Your organization remains responsible for human review, verification, and approval before submitting any output in a tender, grant, procurement, or contractual process.',
  },
  {
    clause: 'SaaS Agreement Clause 2.3(j)–(l) / Terms Clause 8(a) & 14(j)–(l)',
    title: 'October 2026 Google Cloud Agentic AI Responsible Use Flow-Downs',
    tag: 'Acceptable Use',
    summary:
      'Aligns with the October 12, 2026 Google Cloud Terms of Service and Agentic AI Responsible Use Guidelines: requires users to be 18+, and prohibits extracting model weights, training competing AI models on platform outputs, prompt injection/data poisoning, bypassing safety filters or confirmation gates, and using the platform for clinical medical advice, ITAR, or High-Risk Activities.',
  },
  {
    clause: 'SaaS Agreement Clause 5.8 & Clause 21.7',
    title: '30-Day Advance Written Notice for Pricing & General Term Updates',
    tag: 'Notice Guarantee',
    summary:
      'Confirms that Schedule 1 Order Form commercial details cannot be amended without mutual written agreement, and guarantees at least 30 days’ prior written notice before any updates to the standard Pricing Table or general Platform terms take effect.',
  },
];

export function calculateNoticeDaysRemaining(
  lastContractNoticeSentAt?: string,
  nowMs: number = Date.now()
): number {
  const baselineStr = lastContractNoticeSentAt || DEFAULT_V2_RELEASE_DATE;
  const parsedMs = Date.parse(baselineStr);
  const startMs = Number.isNaN(parsedMs) ? Date.parse(DEFAULT_V2_RELEASE_DATE) : parsedMs;
  const elapsedDays = Math.floor(Math.max(0, nowMs - startMs) / (1000 * 60 * 60 * 24));
  return Math.max(0, NOTICE_PERIOD_DAYS - elapsedDays);
}

const TermsUpdateBanner: React.FC = () => {
  const { account, refreshUserDetails } = useAccount();
  const { showSnackbar } = useSnackbar();
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptedLocally, setAcceptedLocally] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  if (account?.role !== 'client_admin' || acceptedLocally) {
    return null;
  }

  const isAlreadySignedLatest =
    account.saasAgreementVersion === LATEST_SAAS_AGREEMENT_VERSION &&
    account.saasAgreementStatus === 'signed';

  if (isAlreadySignedLatest) {
    return null;
  }

  const daysRemaining = calculateNoticeDaysRemaining(account.lastContractNoticeSentAt);
  const isGracePeriodExpired = daysRemaining <= 0;
  const isModalOpen = isDetailsModalOpen || isGracePeriodExpired;

  const handleAcceptUpdatedTerms = async () => {
    setIsAccepting(true);
    try {
      const nowIso = new Date().toISOString();
      const response = await apiService.updateAccountDetails({
        client: {
          saasAgreementVersion: LATEST_SAAS_AGREEMENT_VERSION,
          saasAgreementStatus: 'signed',
          saasAgreementSignedAt: nowIso,
        },
      });

      if (response?.success !== false) {
        setAcceptedLocally(true);
        setIsDetailsModalOpen(false);
        showSnackbar(
          `Thank you — ${LATEST_SAAS_AGREEMENT_VERSION} SaaS Agreement & Terms acknowledged.`,
          'success'
        );
        await refreshUserDetails(true);
      } else {
        showSnackbar('Could not record terms acknowledgment. Please try again.', 'error');
      }
    } catch (_error) {
      showSnackbar('Failed to acknowledge updated terms. Please try again.', 'error');
    } finally {
      setIsAccepting(false);
    }
  };

  return (
    <>
      <Box sx={{ width: '100%', zIndex: 1200 }}>
        <Alert
          severity={isGracePeriodExpired ? 'warning' : 'info'}
          icon={<GavelIcon fontSize="inherit" />}
          sx={{
            borderRadius: 0,
            borderBottom: '1px solid',
            borderColor: isGracePeriodExpired ? 'warning.light' : 'info.light',
            alignItems: 'center',
            py: 1,
            px: 3,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              width: '100%',
            }}
          >
            <Box>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.25 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Updated SaaS Agreement &amp; Platform Terms ({LATEST_SAAS_AGREEMENT_VERSION})
                </Typography>
                <Chip
                  icon={<ClockIcon fontSize="small" />}
                  label={
                    isGracePeriodExpired
                      ? 'Action Required: 30-day review window ended'
                      : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left to review`
                  }
                  size="small"
                  color={isGracePeriodExpired ? 'warning' : 'info'}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                We have updated our SaaS Agreement and Platform Terms to codify{' '}
                <strong>100% Customer IP ownership of generated bid outputs</strong>,{' '}
                <strong>Google Cloud KMS Envelope Encryption &amp; zero AI training</strong>,{' '}
                <strong>Human-in-the-Loop AI governance</strong>, and a{' '}
                <strong>30-day advance notice guarantee</strong>.{' '}
                {isGracePeriodExpired
                  ? 'The 30-day review period has concluded — please accept the updated terms to continue using the platform.'
                  : `You have ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} to review these updates before acceptance is required.`}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0, alignItems: 'center' }}>
              <Button
                variant="outlined"
                color={isGracePeriodExpired ? 'warning' : 'info'}
                size="small"
                startIcon={<InfoIcon fontSize="small" />}
                onClick={() => setIsDetailsModalOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                View Specific Changes (5)
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={isAccepting}
                onClick={handleAcceptUpdatedTerms}
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={
                  isAccepting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <CheckIcon fontSize="small" />
                  )
                }
              >
                {isAccepting ? 'Recording...' : 'Acknowledge & Accept v2.0'}
              </Button>
            </Stack>
          </Box>
        </Alert>
      </Box>

      <Dialog
        open={isModalOpen}
        onClose={() => {
          if (!isAccepting && !isGracePeriodExpired) {
            setIsDetailsModalOpen(false);
          }
        }}
        disableEscapeKeyDown={isGracePeriodExpired}
        maxWidth="md"
        fullWidth
        aria-labelledby="terms-update-dialog-title"
      >
        <DialogTitle
          id="terms-update-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon color="primary" />
            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
              Summary of SaaS Agreement &amp; Platform Terms Updates
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Chip
              icon={<ClockIcon fontSize="small" />}
              label={
                isGracePeriodExpired
                  ? 'Acceptance Required to Continue'
                  : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining in review window`
              }
              color={isGracePeriodExpired ? 'warning' : 'info'}
              size="small"
            />
            <Chip
              label={LATEST_SAAS_AGREEMENT_VERSION}
              color="primary"
              size="small"
              variant="outlined"
            />
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {isGracePeriodExpired && (
            <Alert severity="warning" sx={{ mb: 2.5 }}>
              The 30-day advance notice window for <strong>{LATEST_SAAS_AGREEMENT_VERSION}</strong>{' '}
              has concluded. As an organization administrator, please review the specific updates
              below and click <strong>Acknowledge &amp; Accept</strong> to continue using BIDLOGIQ.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Below is the clause-by-clause breakdown of the updates introduced in{' '}
            <strong>{LATEST_SAAS_AGREEMENT_VERSION}</strong>. These updates strengthen your
            intellectual property protections, codify our Google Cloud KMS encryption and
            no-training guarantees, and align platform usage with the October 12, 2026 Google Cloud
            Agentic AI Responsible Use Guidelines.
          </Typography>

          <Stack spacing={2} divider={<Divider flexItem />}>
            {V2_TERM_CHANGES.map((item) => (
              <Box key={item.clause}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  <Chip label={item.tag} size="small" color="info" variant="outlined" />
                </Box>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'primary.main', fontWeight: 600, mb: 0.5 }}
                >
                  {item.clause}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.summary}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Want to read the full legal text or download our security architecture briefing?
            </Typography>
            <Stack direction="row" spacing={2}>
              <Link
                component={RouterLink}
                to="/terms-conditions"
                target="_blank"
                rel="noopener"
                underline="hover"
                sx={{ fontWeight: 600, fontSize: '0.875rem' }}
              >
                Full Platform Terms &amp; Conditions
              </Link>
              <Link
                href="https://www.bidlogiq.ai/privacy-security-fact-sheet"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontWeight: 600, fontSize: '0.875rem' }}
              >
                Privacy &amp; Security Fact Sheet
              </Link>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            justifyContent: isGracePeriodExpired ? 'flex-end' : 'space-between',
          }}
        >
          {!isGracePeriodExpired && (
            <Button
              onClick={() => setIsDetailsModalOpen(false)}
              disabled={isAccepting}
              color="inherit"
            >
              Review Later ({daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left)
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={isAccepting}
            onClick={handleAcceptUpdatedTerms}
            startIcon={
              isAccepting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckIcon fontSize="small" />
              )
            }
          >
            {isAccepting
              ? 'Recording Acceptance...'
              : `Acknowledge & Accept ${LATEST_SAAS_AGREEMENT_VERSION}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TermsUpdateBanner;
